import { Injectable } from '@angular/core';

export interface CarrierConnectionV2 {
  mode: 'api' | 'manual';
  status: 'connected';
  accountNumber?: string;
  contactName?: string;
  clientId?: string;
  clientSecret?: string;
  env?: 'Sandbox' | 'Production';
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  service?: string;
}

export type CarrierId = 'ups' | 'usps';

export interface ShippingOrderV2 {
  id: string;
  customer: string;
  itemsSummary: string;
  address: string;
  step: -1 | 0 | 1 | 2 | 3; // -1 = unfulfilled, 0..3 = label purchased..delivered
  carrierId: CarrierId | null;
  tracking: string | null;
  service: string | null;
  price: number | null;
  weight?: string;
}

/**
 * A single shipping zone as edited in the V2 Shipping rules tab.
 * Mirrors V1's zone model (webstore/shipping) so it round-trips through the same API.
 * `rateType: 'live'` maps to V1's USPS/calculated shipping type.
 * `markup`/`fallback` are kept for parity with the mockup but are USPS-only-era client fields
 * (no backend column yet) — see TODO markers where they are hidden.
 */
export interface ShippingZoneV2 {
  id: string | null;
  zoneName: string;
  isEnabled: boolean;
  expanded: boolean;
  countries: string[];
  states: string[];
  allStates: boolean;
  rateType: 'flat' | 'free' | 'live';
  flatCost: number;
  pickup: boolean;
  pickupCost: number;
  // USPS-only carrier config for this zone (live/calculated rates)
  uspsEnabled: boolean;
  uspsServices: string[];
  // hidden-for-now (kept for when UPS / markup / fallback ship) — TODO re-enable
  markup: string;
  fallback: string;
  // nested ids echoed back from the API so updates patch in place
  flatRateId: string | null;
  storePickUpId: string | null;
  uspsId: string | null;
  uspsUserID: string | null;
}

// USPS service catalog — value codes match what V1's backend stores in usps.enabledServices.
export const SCV2_USPS_SERVICES: { label: string; value: string }[] = [
  { label: 'Express Mail', value: 'express_mail' },
  { label: 'Priority Mail', value: 'priority_mail' },
  { label: 'First Class', value: 'first_class' },
  { label: 'Ground Advantage', value: 'ground_advantage' },
  { label: 'Standard Post', value: 'standard_post' },
  { label: 'Media Mail', value: 'media_mail' },
  { label: 'Library Mail', value: 'library_mail' },
];

@Injectable({ providedIn: 'root' })
export class ShippingConfigV2Service {
  // Real value is hydrated from webstore/shipping by ShippingConfigurationV2Component.ngOnInit();
  // this default only covers the brief window before that load resolves.
  shippingEnabled = false;
  activeSubTab: 'carriers' | 'rules' | 'dashboard' = 'carriers';

  // Start with nothing connected. (The Connect-carriers tab is still mock/no-backend; USPS live API
  // credentials require a US business account, so this stays empty until that path exists.)
  connections: { ups: CarrierConnectionV2 | null; usps: CarrierConnectionV2 | null } = {
    usps: null,
    ups: null,
  };

  // Zones are loaded from / saved to the real webstore/shipping API by ShippingRulesV2Component.
  // First version is USPS-only; UPS lives behind a flag (see SCV2_UPS_ENABLED).

  orders: ShippingOrderV2[] = [
    { id: '1046', customer: 'Sarah Lin', itemsSummary: '1 item · $18.00', address: '4521 Birchwood Ave, Austin, TX 73301', step: -1, carrierId: null, tracking: null, service: null, price: null },
    { id: '1045', customer: 'Diego Ruiz', itemsSummary: '2 items · $34.50', address: '118 Cedar Ln, Denver, CO 80202', step: -1, carrierId: null, tracking: null, service: null, price: null },
    { id: '1041', customer: 'James Carter', itemsSummary: '1 item · $22.00', address: '77 Harbor View Rd, Seattle, WA 98101', step: 0, carrierId: 'usps', tracking: '9405511899223344', service: 'USPS Priority Mail', price: 7.4 },
    { id: '1039', customer: 'Maria Gomez', itemsSummary: '3 items · $61.20', address: '902 Maple Grove Dr, Chicago, IL 60601', step: 1, carrierId: 'ups', tracking: '1Z9K2W4400112233', service: 'UPS Ground', price: 9.2 },
    { id: '1035', customer: 'Tom Becker', itemsSummary: '1 item · $18.00', address: '44 Lakeview Ct, Portland, OR 97201', step: 3, carrierId: 'usps', tracking: '9405511899119900', service: 'USPS Priority Mail', price: 7.4 },
  ];

  updateOrder(id: string, patch: Partial<ShippingOrderV2>): void {
    const order = this.orders.find((o) => o.id === id);
    if (order) {
      Object.assign(order, patch);
    }
  }
}

// Static carrier metadata, ported 1:1 from the mockup's CARRIERS constant.
export const SCV2_CARRIERS: Record<CarrierId, { name: string; tagline: string; color: string }> = {
  ups: { name: 'UPS', tagline: 'United Parcel Service', color: '#3b2412' },
  usps: { name: 'USPS', tagline: 'U.S. Postal Service', color: '#0b3d78' },
};

export const SCV2_STORE_NAME = 'Cedar Creek Coffee Roasters';
export const SCV2_STORE_ADDRESS = '215 Industrial Pkwy, Lahore, Punjab 54000, PK';

/**
 * First version is USPS-only. UPS (and the live-rate handling fee / fallback fields that go
 * with multi-carrier live rates) are hidden behind this flag but the code is kept intact —
 * flip to `true` once the backend supports UPS to bring the UI back. TODO re-enable UPS.
 */
export const SCV2_UPS_ENABLED = false;

/**
 * Shipping labels dashboard (sub-tab "3. Shipping labels") is hidden for the first version.
 * The component and code are kept intact — flip to `true` to bring the tab back. TODO re-enable labels dashboard.
 */
export const SCV2_LABELS_DASHBOARD_ENABLED = false;

/**
 * Shipping carriers sub-tab (sub-tab "1. Connect carriers" — the USPS/UPS connect flow) is hidden
 * for the first version. The component and code are kept intact — flip to `true` to bring the tab
 * back. TODO re-enable carriers tab.
 */
export const SCV2_CARRIERS_TAB_ENABLED = false;
